import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useCompletion } from '../contexts/CompletionContext';
import { weeks } from '../data/calendar';
import type { Pillar } from '../types/content';
import { countByPillar, groupByDay, pillarStyles } from '../utils/stats';
import type { DeadlineStatus } from '../utils/status';
import { getStatus } from '../utils/status';
import { SlotCard } from './SlotCard';
import { StatusLegend } from './StatusLegend';
const PILLARS: Pillar[] = ['Evergreen', 'Product', 'Campaign'];
export function WeekBoard() {
  const [weekId, setWeekId] = useState(weeks[0].id);
  const [filter, setFilter] = useState<Pillar | 'All'>('All');
  const {
    isDone
  } = useCompletion();
  const week = weeks.find(w => w.id === weekId) ?? weeks[0];
  const counts = useMemo(() => countByPillar(week.slots), [week]);
  const statusCounts = useMemo(() => {
    const base: Record<DeadlineStatus, number> = {
      clear: 0,
      soon: 0,
      critical: 0,
      done: 0
    };
    week.slots.forEach(s => {
      base[getStatus(s.date, isDone(s.id ?? ''))] += 1;
    });
    return base;
  }, [week, isDone]);
  const days = useMemo(() => groupByDay(filter === 'All' ? week.slots : week.slots.filter(s => s.pillar === filter)), [week, filter]);
  return <div className="space-y-5" data-id="element-138" data-mp="g3hha0pshh3r">
      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Weeks" data-id="element-139" data-mp="kzfyn4cp9yuo">
        {weeks.map(w => {
        const isActive = w.id === weekId;
        return <button key={w.id} role="tab" aria-selected={isActive} type="button" onClick={() => setWeekId(w.id)} className={`relative rounded-lg px-3 py-2 text-left transition-colors ${isActive ? 'text-chalk' : 'text-mute hover:text-chalk'}`} data-id="element-140" data-mp="1dowxajqwybzc">
              {isActive && <motion.span layoutId="week-pill" className="absolute inset-0 rounded-lg border border-ink-600 bg-ink-800" transition={{
            type: 'spring',
            stiffness: 400,
            damping: 34
          }} data-id="element-141" data-mp="1wf4n605lr5xl" />}
              <span className="relative block text-[12px] font-medium leading-none" data-id="element-142" data-mp="21a7b24jlti0p">{w.label}</span>
              <span className="relative mt-1 block font-mono text-[10px] leading-none text-faint" data-id="element-143" data-mp="1wfa556hmfos1">{w.range.replace('Mon ', '').replace('Sun ', '')}</span>
            </button>;
      })}
      </div>

      <div className="rounded-2xl border border-ink-700 bg-ink-850 shadow-panel" data-id="element-144" data-mp="1nec0qn39tf8x">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-ink-700/70 px-5 py-4" data-id="element-145" data-mp="1ikhjdqb51cd0">
          <div className="max-w-xl" data-id="element-146" data-mp="1dkd0xuqo67k5">
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-faint" data-id="element-147" data-mp="18oe5xoau5rar">{week.range}</p>
            <h2 className="mt-1.5 font-display text-2xl leading-tight text-chalk" data-id="element-148" data-mp="14maugzo62mph">{week.headline}</h2>
            <p className="mt-2 font-mono text-[11px] text-mute" data-id="element-149" data-mp="zmoietk2rci9">
              {week.slots.length} anchor slots · {counts.Evergreen} evergreen · {counts.Product} product · {counts.Campaign} campaign
            </p>
            <div className="mt-3" data-id="element-150" data-mp="3g81sc4v54nqe">
              <StatusLegend counts={statusCounts} data-id="element-151" data-mp="3gb7k5rv5m3pt" />
            </div>
          </div>

          <div className="flex gap-1.5" data-id="element-152" data-mp="3kbrdekclwn6o">
            {(['All', ...PILLARS] as const).map(p => {
            const isActive = filter === p;
            return <button key={p} type="button" onClick={() => setFilter(p)} aria-pressed={isActive} className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.12em] transition-colors ${isActive ? p === 'All' ? 'border-ink-500 bg-ink-700 text-chalk' : `${pillarStyles[p].border} ${pillarStyles[p].bg} ${pillarStyles[p].text}` : 'border-ink-700 text-faint hover:text-chalk'}`} data-id="element-153" data-mp="2skts41ckk4v8">
                  {p}
                </button>;
          })}
          </div>
        </div>

        <div className="overflow-x-auto px-5 py-5" data-id="element-154" data-mp="fb9any0o0hqd">
          <div className="flex min-w-full gap-4" data-id="element-155" data-mp="t5imajnqpa1x">
            {days.map(d => <div key={d.date} className="w-[260px] shrink-0" data-id="element-156" data-mp="odihodwq5sz0">
                <div className="mb-3 flex items-baseline justify-between border-b border-ink-700 pb-2" data-id="element-157" data-mp="obxqmy9mcs5w">
                  <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-chalk" data-id="element-158" data-mp="y3k6j7wwjfez">{d.day}</span>
                  <span className="font-mono text-[10px] text-faint" data-id="element-159" data-mp="1c332l2cfv0ax">{d.slots.length}</span>
                </div>
                <div className="space-y-2.5" data-id="element-160" data-mp="181huduauna72">
                  {d.slots.map((s, i) => <SlotCard key={s.id ?? `${d.date}-${i}`} slot={s} index={i} data-id="element-161" data-mp="3du6gc0hmkuzp" data-mpi={i} />)}
                </div>
              </div>)}
            {days.length === 0 && <p className="py-8 text-sm text-mute" data-id="element-162" data-mp="2zpfrrmoyy9dc">No {filter.toLowerCase()} anchor slots this week.</p>}
          </div>
        </div>
      </div>
    </div>;
}